import { Component } from '@theme/component';

class RosterCRT extends Component {
  connectedCallback() {
    super.connectedCallback();
    if (this.controller) return;
    this.controller = new AbortController();
    this.channels = [...this.querySelectorAll('[data-roster-channel]')];
    this.portrait = this.querySelector('[data-roster-portrait]');
    this.enter = this.querySelector('[data-roster-enter]');
    this.selected = this.channels.find(link => link.hasAttribute('data-selected')) || this.channels[0];
    this.sequence = 0;
    this.pointerPosition = null;
    this.touch = null;
    const options = { signal: this.controller.signal };
    for (const link of this.channels) {
      link.addEventListener('focus', () => this.select(link), options);
      link.addEventListener('pointerdown', event => {
        this.touch = event.pointerType === 'touch' ? { link, selected: this.selected } : null;
      }, options);
      link.addEventListener('pointercancel', () => { this.touch = null; }, options);
      link.addEventListener('click', event => {
        if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        if (this.touch?.link === link && this.touch.selected !== link && event.detail !== 0) event.preventDefault();
        this.touch = null;
        this.select(link);
      }, options);
      link.addEventListener('keydown', event => this.move(event, link), options);
    }
    this.addEventListener('pointermove', event => {
      const previous = this.pointerPosition;
      this.pointerPosition = { x: event.clientX, y: event.clientY };
      if (event.pointerType === 'touch' || previous?.x === event.clientX && previous?.y === event.clientY) return;
      const link = event.target.closest('[data-roster-channel]');
      if (link && this.channels.includes(link)) this.select(link);
    }, options);
    delete this.dataset.enhanced;
    const requested = new URL(location.href).searchParams.get('artist');
    const target = this.channels.find(link => link.dataset.handle === requested);
    if (target) { this.select(target); target.scrollIntoView({ block: 'nearest' }); }
    else this.select(this.selected);
    this.dataset.enhanced = '';
  }

  updatedCallback() {
    super.updatedCallback();
    this.disconnectedCallback();
    this.connectedCallback();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    this.catalog = null;
    this.sequence++;
    clearTimeout(this.tuneTimer);
    delete this.dataset.changing;
    super.disconnectedCallback();
  }

  move(event, link) {
    const index = this.channels.indexOf(link);
    let next;
    if (event.key === 'ArrowDown') next = (index + 1) % this.channels.length;
    else if (event.key === 'ArrowUp') next = (index - 1 + this.channels.length) % this.channels.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = this.channels.length - 1;
    else if (event.key === 'ArrowRight') { event.preventDefault(); this.enter?.focus(); return; }
    else return;
    event.preventDefault();
    this.channels[next].focus({ preventScroll: true });
    this.channels[next].scrollIntoView({ block: 'nearest', behavior: 'instant' });
  }

  select(link) {
    if (!link || !this.portrait || !this.enter || this.selected === link && this.dataset.enhanced !== undefined) return;
    const template = link.parentElement.querySelector('[data-roster-template]');
    if (!(template instanceof HTMLTemplateElement)) return;
    for (const item of this.channels) item.toggleAttribute('data-selected', item === link);
    this.selected = link;
    const sequence = ++this.sequence;
    this.portrait.replaceChildren(template.content.cloneNode(true));
    const image = this.portrait.querySelector('img');
    if (image) image.loading = 'eager';
    this.enter.href = link.href;
    this.querySelector('[data-roster-name]').textContent = link.dataset.name;
    this.querySelector('[data-roster-position]').textContent = `${link.dataset.position.padStart(2, '0')} / ${this.channels.length}`;
    this.querySelector('[data-roster-status]').textContent = `${link.dataset.name}. ${this.enter.textContent.trim()}`;
    this.dataset.changing = '';
    clearTimeout(this.tuneTimer);
    this.tuneTimer = setTimeout(() => { delete this.dataset.changing; }, 240);
    if (!image) this.loadArtwork(link.dataset.handle, sequence);
  }

  async loadArtwork(handle, sequence) {
    try {
      if (!this.catalog) {
        this.catalog = fetch(this.dataset.manifest, { credentials: 'omit', signal: this.controller.signal })
          .then(response => { if (!response.ok) throw Error('CATALOG_UNAVAILABLE'); return response.json(); });
      }
      const catalog = await this.catalog;
      if (!this.isConnected || sequence !== this.sequence || catalog.version !== 1 || !Array.isArray(catalog.artists)) return;
      const matches = catalog.artists.filter(artist => artist.handle === handle);
      if (matches.length !== 1) return;
      const art = matches[0].artwork;
      if (!art || art.classification !== 'flat-room-artwork' || !['approved', 'draft'].includes(art.status) || !Number.isInteger(art.width) || art.width <= 0 || !Number.isInteger(art.height) || art.height <= 0) return;
      const url = new URL(art.src, location.href);
      if (url.protocol !== 'https:' || url.username || url.password) return;
      const image = new Image(art.width, art.height);
      image.alt = '';
      image.src = url.href;
      image.dataset.rosterArtwork = '';
      await image.decode();
      if (!this.isConnected || sequence !== this.sequence) return;
      this.portrait.querySelector('[data-roster-no-photo]')?.replaceWith(image);
      this.portrait.querySelector('[data-roster-media-label]').textContent = this.dataset.artworkLabel;
    } catch { this.catalog = null; }
  }
}

if (!customElements.get('anr-roster-crt')) customElements.define('anr-roster-crt', RosterCRT);
